// @ts-nocheck
import {
    ActionReasonDto,
    EmployeeDto,
    InventoryDto, StocktakeDto,
    StocktakeItemDto
} from "@/PageComponents/Stock Take/StockTake.model";
import StocktakeForm from "@/PageComponents/Stock Take/StockTakeForm";
import {Box, Card, Flex} from "@mantine/core";
import React, {useContext, useEffect, useMemo, useState} from "react";
import SCMessageBarContext from "@/utils/contexts/sc-message-bar-context";
import constants from "@/utils/constants";
import Breadcrumbs from "@/components/breadcrumbs";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {notifications} from "@mantine/notifications";
import StockService from "@/services/stock/stock-service";
import {useRouter} from "next/router";
import Fetch from "@/utils/Fetch";
import * as Enums from "@/utils/enums";
import Storage from "@/utils/storage";
import Helper from "@/utils/helper";
import PermissionService from "@/services/permission/permission-service";
import featureService from "@/services/feature/feature-service";
import { createFileRoute } from '@tanstack/react-router'

interface StocktakeItemFormProps {
    onSubmit: (values: Partial<StocktakeItemDto>) => void;
    onCancel: () => void;
    stocktakeId: string;
    inventoryItems: InventoryDto[];
    actionReasons: ActionReasonDto[];
    employees: EmployeeDto[];
    initialValues?: Partial<StocktakeItemDto>;
}

function EditStockPage() {

    const router = useRouter();

    // Fetch the feature flag to check if stock take is enabled.
    // Redirect to home page if the feature is not available.
    const hasStockTakeQuery = useQuery(['hasStockTake'], () => featureService.getFeature(constants.features.STOCK_TAKE))

    // Redirect if stock take feature is not available
    useEffect(() => {
        if (hasStockTakeQuery.data === null) {
            router.replace('/');
        }
    }, [hasStockTakeQuery.data])

    const { draftData } = router.query;
 // TODO(tanstack-router): verify router.query → useParams/useSearch

    const queryClient = useQueryClient();

    const createFormData = useMemo(() => draftData && decodeURIComponent(draftData as string) ? JSON.parse(decodeURIComponent(draftData as string)) : null, [])

    const stocktakeMutation = useMutation({
        mutationFn: StockService.postStockTake,
        onSuccess: (data: any) => {
            // Invalidate and refetch relevant queries
            queryClient.invalidateQueries({ queryKey: ['stocktake'] });

            notifications.show({
                title: "Success",
                message: "Stock take has been updated successfully",
                color: "scBlue"
            });

            router.replace(`/stock-take/${data.ID}`);
        },
        onError: (error: Error) => {
            notifications.show({
                title: "Error",
                message: error.message || "Failed to update stock take",
                color: "yellow"
            });
        }
    });

    const { data: stocktake, isLoading } = useQuery<StocktakeDto>({
        initialData: createFormData,
        queryKey: ['stockTakeItems', router.query.id],
 // TODO(tanstack-router): verify router.query → useParams/useSearch
        queryFn: async () => {
            // Make the API call to fetch StockTakeItems for the given stockTake ID
            const response = await Fetch.get({
                url: `/Stocktake/` + router.query.id,
 // TODO(tanstack-router): verify router.query → useParams/useSearch
            });

            if (!response.ID) {
                throw new Error(response.serverMessage || response.message || 'Failed to fetch stock take items');
            }

            return response;
        },
        // Only run the query if we have a router.query.id AND router is ready
 // TODO(tanstack-router): verify router.query → useParams/useSearch
        enabled: router.isReady && !!router.query.id,
 // TODO(tanstack-router): verify router.query → useParams/useSearch
        refetchOnWindowFocus: false,
        staleTime: 0,
        cacheTime: 0
    });

    const messageBarContext = useContext(SCMessageBarContext);

    const onSubmit = (values: any) => {
        stocktakeMutation.mutate(values);
    }

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const [stockAdmin] = useState(PermissionService.hasPermission(Enums.PermissionName.StockTakeManager));

    useEffect(() => {
        getAccessStatus();
    }, []);

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess || !stockAdmin) {
            Helper.nextRouter(router.replace, "/");
        }

    }, [accessStatus, stockAdmin]);

    //TODO(tanstack-router): verify router.query → useParams/useSearch
    return (
        <Box>
            <Flex h={28} align={'center'} px={'lg'}>
                <Breadcrumbs currPage={{ text: 'Edit Stocktake', link: `/stock-take/${router.query.id}/edit`, type: 'edit' }} />
            </Flex>
            <Box
                mih={`calc(100vh - 40px - 30px${((messageBarContext as any)?.isActive ? ` - ${constants.messageBarMargin}px` : "")})`}
                bg={'gray.1'}
                py={{base: 0, lg: 'md', xl: 'lg'}}
            >
                <Card mx={'auto'} bg={'white'} p={'md'} mt={0} shadow={'xs'} radius={5} maw={1100}>
                    {
                        (createFormData || stocktake) &&
                        <StocktakeForm
                            submitting={stocktakeMutation.isLoading}
                            initialValues={createFormData || stocktake}
                            onSubmit={onSubmit}
                        />
                    }
                </Card>
            </Box>
        </Box>
    );
}
            

export default EditStockPage;

export const Route = createFileRoute('/_authenticated/stock-take/$id/edit')({ component: EditStockPage })
